import { FacetedFilter } from '@app/components/data-table/faceted-filter';
import { useAnalytics } from '@app/services/analytics/AnalyticsContext';
import { Option } from '@app/types/data-table';
import {
  InsightsAvailableFilters,
  InsightsFilterOption,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import { Button } from '@shared/components/ui/button';
import {
  useInsightsFilters,
  useInsightsTableState,
  useInsightsTaxonomy,
} from '../hooks/use-insights-filters';
import { InsightsControlLabel } from './insights-explore-pills';
import { InsightsItemFilter } from './insights-item-filter';
import { InsightsTaxonomyFilter } from './insights-taxonomy-filter';

const toOptions = (rows?: readonly InsightsFilterOption[]): Option[] =>
  (rows ?? []).map((row) => ({ value: row.uuid, label: row.name }));

/**
 * Filter pills, one per dimension, with options served by the response's
 * availableFilters. Site, supplier and item are multi-select; the product
 * taxonomy is a single path down its three tiers. Any change resets the table
 * to page 1.
 */
export function InsightsFilters({
  available,
}: {
  available?: InsightsAvailableFilters;
}) {
  const [filters, setFilters] = useInsightsFilters();
  const { values: taxonomy, clear: clearTaxonomy } = useInsightsTaxonomy();
  const [, setTableState] = useInsightsTableState();
  const { track } = useAnalytics();

  const apply = (patch: Parameters<typeof setFilters>[0]) => {
    setFilters(patch);
    setTableState({ page: null });
  };

  const applyTracked = (
    filterType: 'site' | 'supplier',
    values: string[],
    options: Option[]
  ) => {
    apply({ [filterType]: values.length ? values : null });
    track('Applied PG Insights Filter', {
      filter_type: filterType,
      value_names: values.map(
        (value) => options.find((option) => option.value === value)?.label
      ),
      value_count: values.length,
    });
  };

  const anyActive =
    filters.site.length > 0 ||
    filters.supplier.length > 0 ||
    filters.item.length > 0 ||
    !!taxonomy.productCategory ||
    !!taxonomy.productSubCategory ||
    !!taxonomy.productBase;

  const siteOptions = toOptions(available?.sites);
  const supplierOptions = toOptions(available?.suppliers);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <InsightsControlLabel>Filter</InsightsControlLabel>
      <InsightsTaxonomyFilter />
      <FacetedFilter
        title="Site"
        itemName="site"
        multiple
        options={siteOptions}
        selectedValues={filters.site}
        onChange={(values) => applyTracked('site', values, siteOptions)}
      />
      <FacetedFilter
        title="Supplier"
        itemName="supplier"
        multiple
        options={supplierOptions}
        selectedValues={filters.supplier}
        onChange={(values) => applyTracked('supplier', values, supplierOptions)}
      />
      <InsightsItemFilter />
      {anyActive && (
        <Button
          aria-label="Reset filters"
          variant="outline"
          size="sm"
          className="border-dashed"
          icon={<Icons.x />}
          onClick={() => {
            apply({ site: null, supplier: null, item: null });
            clearTaxonomy();
            track('Cleared PG Insights Filters');
          }}
        >
          Reset
        </Button>
      )}
    </div>
  );
}
